import { ProgressMeter } from '../../components/ui/ProgressMeter';

// ------------------------------------------------------------------
// ReportScoreStrip — a report's earned-vs-possible score, as a strip a
// screen reader can read (story 4-81; P06 §4).
//
// WHY A PRIMITIVE. Both the web report renderer (`modules/reports/
// components/ReportRender.tsx`) and the template editor's live preview need
// the SAME "5/7 (71%)" strip once story 4-80 made the number real (earned
// against what was actually captured, never the template's theoretical
// max) — composing it here once, on top of the existing `ProgressMeter`
// primitive, is what stops it from becoming a second bespoke bar.
//
// HONESTY OVER a fake bar. `possible <= 0` (no score-eligible field was ever
// answered) renders a plain sentence, not a 100%-filled bar: a filled bar
// reads as "everything scored perfectly", which is not what "nothing to
// score" means. This mirrors `ProgressMeter`'s own "0 of 0 is COMPLETE, not
// empty" rule by choosing not to invoke the bar at all when there is no
// meaningful denominator, rather than picking one of that rule's two
// misleading readings.
//
// COLOUR: `ProgressMeter` already draws its fill from semantic tokens
// (`bg-primary`/`bg-success`) — nothing here introduces a new hardcoded
// colour, so this stays covered by `scripts/contrast-check.mjs` through the
// same text roles every other primitive uses.

export interface ReportScoreStripProps {
  earned: number;
  possible: number;
  /** `earned / possible * 100`, one decimal place. Null when `possible` is 0. */
  percentage: number | null;
  className?: string;
}

export function ReportScoreStrip({ earned, possible, percentage, className = '' }: ReportScoreStripProps): JSX.Element {
  if (!Number.isFinite(possible) || possible <= 0) {
    return (
      <p className={`text-sm text-muted-foreground ${className}`}>No score-eligible questions were answered.</p>
    );
  }

  return (
    <div className={`space-y-1.5 ${className}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-foreground">
          {`${earned} / ${possible} points`}
        </span>
        {percentage != null && (
          <span className="text-sm font-semibold tabular-nums text-muted-foreground">{`${percentage}%`}</span>
        )}
      </div>
      <ProgressMeter
        value={earned}
        total={possible}
        label="score"
        valueText={`${earned} of ${possible} points scored${percentage != null ? `, ${percentage} percent` : ''}`}
      />
    </div>
  );
}
